import { cn } from '@/lib/utils';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { Region } from '@chiroup/core/types/Condition.type';
import React, { useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { REGION_OPTIONS, ROM_TESTS_PER_REGION } from '../../../../../constants';
import UnifiedRangeOfMotionRegion from './UnifiedRangeOfMotionRegion';

type Props = {
  value: any;
  onChange: (val: any) => void;
  romSliderStep: any;
  signed: boolean;
  close?: () => void;
  /** Hides the Remove region button (trial SOAP sandbox). */
  hideRemoveRegion?: boolean;
  /** Trial SOAP sandbox: called instead of switching when any region other
   *  than the current one is chosen (a full-EHR pop-up opens). Below sm it
   *  also collapses the region <select> to a compact "Region: <name>" chip
   *  (tap opens the same pop-up). */
  interceptRegionSwitch?: () => void;
};

const UnifiedRangeOfMotion: React.FC<Props> = ({
  value,
  onChange,
  romSliderStep,
  signed,
  close,
  hideRemoveRegion,
  interceptRegionSwitch,
}) => {
  const [currentTab, setCurrentTab] = useState<Region | null>(null);

  const possibleTabs = useMemo(() => {
    return REGION_OPTIONS.filter(region => !!ROM_TESTS_PER_REGION[region.value]).map(region => ({
      name: region.text,
      region: region.value,
    }));
  }, []);

  const tabs = useMemo(() => {
    const tabsInUse = possibleTabs.filter(region => {
      return !!value?.[region.region];
    });

    if (tabsInUse?.length) {
      return tabsInUse;
    }

    return [];
  }, [possibleTabs, value]);

  const tabsLeft = useMemo(() => {
    return possibleTabs.filter(tab => {
      return !tabs?.some(t => t.region === tab.region);
    });
  }, [possibleTabs, tabs]);

  // Show all possible tabs (active + inactive)
  const visibleTabs = useMemo(() => {
    return possibleTabs; // Show all tabs like legacy
  }, [possibleTabs]);

  useEffect(() => {
    if (!currentTab) {
      if (tabs?.length) {
        setCurrentTab(tabs[0]?.region);
      } else {
        setCurrentTab(possibleTabs[0]?.region);
        setCurrentTab(possibleTabs[0]?.region);
      }
    }
  }, [currentTab, possibleTabs, tabs]);

  // Helper function to clean ROM data structure
  const cleanRomData = (data: any): any => {
    if (!data || typeof data !== 'object') return {};

    const cleaned: any = {};

    Object.keys(data).forEach(region => {
      if (data[region] && typeof data[region] === 'object') {
        cleaned[region] = {};

        Object.keys(data[region]).forEach(romTest => {
          const testData = data[region][romTest];

          if (testData && typeof testData === 'object') {
            cleaned[region][romTest] = {};

            // Only keep properly nested side data
            Object.keys(testData).forEach(key => {
              if (
                key !== 'value' &&
                key !== 'pain' &&
                key !== 'side' &&
                typeof testData[key] === 'object' &&
                (testData[key].value !== undefined || testData[key].pain !== undefined)
              ) {
                cleaned[region][romTest][key] = testData[key];
              }
            });
          }
        });

        // Preserve region-level touched flag across normalization
        if (data[region]?.touched === true) {
          cleaned[region].touched = true;
        }
      }
    });

    return cleaned;
  };

  const onChangeDetail = (region: string, prop: string, val: any) => {
    const { side, ...sideData } = val;
    const cleanedValue = cleanRomData(value);
    const newValue = {
      ...cleanedValue,
      [region]: {
        ...cleanedValue?.[region],
        touched: true, // Set touched when user interacts with this region (like legacy)
        [prop]: {
          ...cleanedValue?.[region]?.[prop],
          [side || 'SpinalArea']: sideData,
        },
      },
    };
    onChange(newValue);
  };

  const removeRegion = () => {
    if (currentTab) {
      const newValue = { ...value };
      delete newValue[currentTab];
      onChange(newValue);
      setCurrentTab(tabs.find(tab => tab.region !== currentTab)?.region || null);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <Scrollbars className="min-h-14 w-full" autoHide>
          <div>
            <div className="sm:hidden">
              {interceptRegionSwitch ? (
                <button
                  type="button"
                  onClick={interceptRegionSwitch}
                  className="flex items-center gap-2 p-4 pb-0 text-sm text-gray-500 dark:text-gray-400"
                >
                  Region:
                  <span className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 font-medium text-gray-700 dark:border-dark-gray-600 dark:bg-dark-gray-800 dark:text-gray-200">
                    {visibleTabs.find(tab => tab.region === currentTab)?.name || ''}
                    <span aria-hidden>▾</span>
                  </span>
                </button>
              ) : (
                <>
                  <label htmlFor="tabs" className="sr-only">
                    Select a tab
                  </label>
                  <select
                    id="tabs"
                    name="tabs"
                    className="block w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                    defaultValue={visibleTabs.find(tab => tab.region === currentTab)?.name}
                    onChange={e => {
                      setCurrentTab(e.target.value as Region);
                    }}
                    value={visibleTabs.find(tab => tab.region === currentTab)?.region}
                  >
                    {visibleTabs?.map(tab => (
                      <option key={tab.name} value={tab.region}>
                        {tab.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
            <div className="hidden sm:block">
              <nav className="flex space-x-2" aria-label="Tabs">
                {(tabs || []).map(tab => (
                  <div
                    key={tab.name}
                    className={cn(
                      currentTab === tab.region
                        ? 'bg-white dark:bg-dark-gray-900 border-gray-300 dark:border-dark-gray-700 text-gray-700 dark:text-dark-gray-50'
                        : 'text-gray-500 hover:text-gray-700 dark:text-dark-gray-50 dark:hover:text-dark-gray-100 border-transparent',
                      'px-3 py-2 font-medium text-sm rounded-md cursor-pointer whitespace-nowrap border'
                    )}
                    aria-current={currentTab === tab.region ? 'page' : undefined}
                    onClick={() => {
                      if (interceptRegionSwitch && tab.region !== currentTab) {
                        interceptRegionSwitch();

                        return;
                      }
                      setCurrentTab(tab.region);
                    }}
                  >
                    {tab.name}
                  </div>
                ))}
                {(tabsLeft || []).map(tab => (
                  <div
                    key={tab.name}
                    className={cn(
                      currentTab === tab.region
                        ? 'bg-gray-100 text-gray-700 dark:bg-dark-gray-800 dark:text-dark-gray-100'
                        : 'bg-gray-100 text-gray-300 hover:text-gray-400 dark:bg-dark-gray-700 dark:text-dark-gray-500',
                      'px-3 py-2 font-medium text-sm rounded-md cursor-pointer whitespace-nowrap border border-transparent'
                    )}
                    aria-current={currentTab === tab.region ? 'page' : undefined}
                    onClick={() => {
                      if (interceptRegionSwitch && tab.region !== currentTab) {
                        interceptRegionSwitch();

                        return;
                      }
                      setCurrentTab(tab.region);
                    }}
                  >
                    {tab.name}
                  </div>
                ))}
              </nav>
            </div>
          </div>
        </Scrollbars>
      </div>

      <div className="p-4">
        {currentTab && (
          <UnifiedRangeOfMotionRegion
            value={value || {}}
            onChange={onChangeDetail}
            onChangeComplete={onChange}
            rows={ROM_TESTS_PER_REGION[currentTab]}
            currentTab={currentTab}
            romSliderStep={romSliderStep}
            signed={signed}
          />
        )}
      </div>

      <div className="flex flex-row justify-between p-4">
        {!hideRemoveRegion && (
          <Button
            onClick={removeRegion}
            text="Remove region"
            color={ButtonColors.plainWithBorder}
            disabled={!currentTab || signed}
          />
        )}
        {close && <Button onClick={close} text="Close" color={ButtonColors.plainWithBorder} />}
      </div>
    </div>
  );
};

export default UnifiedRangeOfMotion;
